import type { PageParams } from '../../services/api/chat.api'
import type { InsightsSubject as InsightsKind } from '../../app/modules/types/assetInsights'

export const queryKeys = {
  // Assets/Domains
  assets: {
    all: (orgId: string) => [orgId, 'assets'] as const,
    list: (orgId: string, params: string) => [orgId, 'assets', 'list', params] as const,
    detail: (orgId: string, id: string, customScanIds?: string) =>
      customScanIds
        ? [orgId, 'assets', 'detail', id, customScanIds] as const
        : [orgId, 'assets', 'detail', id] as const,
    filterOptions: (orgId: string) => [orgId, 'assets', 'filterOptions'] as const,
    /** The business-value change trail, one page at a time — see useAssetValue.ts. */
    valueHistory: (orgId: string, id: string, page: number) =>
      [orgId, 'assets', 'valueHistory', id, page] as const,
  },

  // SSL Certificates
  ssl: {
    all: (orgId: string) => [orgId, 'ssl'] as const,
    list: (orgId: string, params: string) => [orgId, 'ssl', 'list', params] as const,
    detail: (orgId: string, id: string) => [orgId, 'ssl', 'detail', id] as const,
    filterOptions: (orgId: string) => [orgId, 'ssl', 'filterOptions'] as const,
  },

  // Uploaded files
  files: {
    all: (orgId: string) => [orgId, 'files'] as const,
    list: (orgId: string, params: string) => [orgId, 'files', 'list', params] as const,
    detail: (orgId: string, id: string) => [orgId, 'files', 'detail', id] as const,
    /** The valuation itself — a file's detail row carries only the tier. */
    value: (orgId: string, id: string) => [orgId, 'files', 'value', id] as const,
    valueHistory: (orgId: string, id: string, page: number) =>
      [orgId, 'files', 'valueHistory', id, page] as const,
  },

  // IP Addresses
  ips: {
    all: (orgId: string) => [orgId, 'ips'] as const,
    list: (orgId: string, params: string) => [orgId, 'ips', 'list', params] as const,
    detail: (orgId: string, id: string, customScanIds?: string) =>
      customScanIds
        ? [orgId, 'ips', 'detail', id, customScanIds] as const
        : [orgId, 'ips', 'detail', id] as const,
    filterOptions: (orgId: string) => [orgId, 'ips', 'filterOptions'] as const,
    /** The business-value change trail, one page at a time — see useAssetValue.ts. */
    valueHistory: (orgId: string, id: string, page: number) =>
      [orgId, 'ips', 'valueHistory', id, page] as const,
  },

  // Vulnerabilities
  vulnerabilities: {
    all: (orgId: string) => [orgId, 'vulnerabilities'] as const,
    list: (orgId: string, params: string) => [orgId, 'vulnerabilities', 'list', params] as const,
    detail: (orgId: string, id: string) => [orgId, 'vulnerabilities', 'detail', id] as const,
    filterOptions: (orgId: string) => [orgId, 'vulnerabilities', 'filterOptions'] as const,
    poc: (orgId: string, pocUrl: string) => [orgId, 'vulnerabilities', 'poc', pocUrl] as const,
    // Page 1 rides along in the detail response; this keys the paged remainder (§4b).
    occurrences: (orgId: string, id: string) =>
      [orgId, 'vulnerabilities', 'occurrences', id] as const,
    comments: (orgId: string, id: string, params: string) =>
      [orgId, 'vulnerabilities', 'comments', id, params] as const,
    timeline: (orgId: string, id: string, params: string) =>
      [orgId, 'vulnerabilities', 'timeline', id, params] as const,
  },

  // Compliance (benchmark control × asset × component)
  // The superuser console. Not organization-scoped, so no orgId in the key.
  platform: {
    all: ['platform'] as const,
    list: (resource: string, qs: string) => ['platform', resource, 'list', qs] as const,
    detail: (resource: string, id: string | number) => ['platform', resource, 'detail', String(id)] as const,
    options: (resource: string) => ['platform', resource, 'options'] as const,
    history: (resource: string, id: string | number, qs: string) =>
      ['platform', resource, 'history', String(id), qs] as const,
    twoFactorMode: ['platform', 'twoFactor', 'mode'] as const,
    userTwoFactor: (id: string) => ['platform', 'users', 'twoFactor', id] as const,
  },
  waf: {
    all: (orgId: string) => [orgId, 'waf'] as const,
    list: (orgId: string, params: string) => [orgId, 'waf', 'list', params] as const,
    detail: (orgId: string, id: string) => [orgId, 'waf', 'detail', id] as const,
    filterOptions: (orgId: string) => [orgId, 'waf', 'filterOptions'] as const,
  },
  loadTest: {
    all: (orgId: string) => [orgId, 'loadTest'] as const,
    list: (orgId: string, params: string) => [orgId, 'loadTest', 'list', params] as const,
    detail: (orgId: string, id: string) => [orgId, 'loadTest', 'detail', id] as const,
    filterOptions: (orgId: string) => [orgId, 'loadTest', 'filterOptions'] as const,
  },
  compliance: {
    all: (orgId: string) => [orgId, 'compliance'] as const,
    list: (orgId: string, params: string) => [orgId, 'compliance', 'list', params] as const,
    detail: (orgId: string, id: string) => [orgId, 'compliance', 'detail', id] as const,
    /** `asset` narrows the options to one asset, so it must split the cache entry. */
    filterOptions: (orgId: string, asset = '') =>
      [orgId, 'compliance', 'filterOptions', asset] as const,
    occurrences: (orgId: string, id: string) =>
      [orgId, 'compliance', 'occurrences', id] as const,
    timeline: (orgId: string, id: string) => [orgId, 'compliance', 'timeline', id] as const,
    comments: (orgId: string, id: string) => [orgId, 'compliance', 'comments', id] as const,
    /** Organization posture now — raw and exception-adjusted. */
    stats: (orgId: string) => [orgId, 'compliance', 'stats'] as const,
    /** Posture per (benchmark, profile); deliberately not per version. */
    benchmarks: (orgId: string) => [orgId, 'compliance', 'benchmarks'] as const,
    scanStats: (orgId: string, scanId: string) =>
      [orgId, 'compliance', 'scanStats', scanId] as const,
  },

  // Issues — the remediation-tracking layer above findings.
  issues: {
    all: (orgId: string) => [orgId, 'issues'] as const,
    /** Prefix for every list page, so one invalidation reaches them all. */
    lists: (orgId: string) => [orgId, 'issues', 'list'] as const,
    list: (orgId: string, params: string) => [orgId, 'issues', 'list', params] as const,
    detail: (orgId: string, id: string) => [orgId, 'issues', 'detail', id] as const,
    facets: (orgId: string) => [orgId, 'issues', 'facets'] as const,
    config: (orgId: string) => [orgId, 'issues', 'config'] as const,
    occurrences: (orgId: string, id: string) => [orgId, 'issues', 'occurrences', id] as const,
    activity: (orgId: string, id: string) => [orgId, 'issues', 'activity', id] as const,
    views: (orgId: string) => [orgId, 'issues', 'views'] as const,
    members: (orgId: string) => [orgId, 'issues', 'members'] as const,
    pick: (orgId: string, q: string) => [orgId, 'issues', 'pick', q] as const,
    slaPolicy: (orgId: string) => [orgId, 'issues', 'slaPolicy'] as const,
  },

  // Ports
  ports: {
    all: (orgId: string) => [orgId, 'ports'] as const,
    list: (orgId: string, params: string) => [orgId, 'ports', 'list', params] as const,
    filterOptions: (orgId: string) => [orgId, 'ports', 'filterOptions'] as const,
  },

  // Emails
  emails: {
    all: (orgId: string) => [orgId, 'emails'] as const,
    list: (orgId: string, params: string) => [orgId, 'emails', 'list', params] as const,
    filterOptions: (orgId: string) => [orgId, 'emails', 'filterOptions'] as const,
  },

  // DNS Records
  dnsRecords: {
    all: (orgId: string) => [orgId, 'dnsRecords'] as const,
    list: (orgId: string, params: string) => [orgId, 'dnsRecords', 'list', params] as const,
    filterOptions: (orgId: string) => [orgId, 'dnsRecords', 'filterOptions'] as const,
  },

  // Workflow
  workflow: {
    all: (orgId: string) => [orgId, 'workflow'] as const,
    list: (orgId: string, params: string) => [orgId, 'workflow', 'list', params, 'detail'] as const,
    detail: (orgId: string, id: string) => [orgId, 'workflow', 'detail', id, 'list'] as const,
    filterOptions: (orgId: string) => [orgId, 'workflow', 'filterOptions'] as const,
    nodeTypes: (orgId: string) => [orgId, 'workflow', 'nodeTypes'] as const,
    versions: (orgId: string, id: string, page: number) =>
      [orgId, 'workflow', 'versions', id, 'list', page] as const,
    version: (orgId: string, id: string, number: number) =>
      [orgId, 'workflow', 'versions', id, 'detail', number] as const,
    nodeAi: {
      status: (orgId: string, nodeTypeId: string) => [orgId, 'workflow', 'nodeAi', 'status', nodeTypeId] as const,
      assist: (orgId: string, nodeTypeId: string) => [orgId, 'workflow', 'nodeAi', 'assist', nodeTypeId] as const,
    },
  },
  // Workflow library
  workflowLibrary: {
    all: (orgId: string) => [orgId, 'workflowLibrary'] as const,
    list: (orgId: string, params: string, page: number) => [orgId, 'workflowLibrary', 'list', params, page, 'detail'] as const,
    detail: (orgId: string, id: string) => [orgId, 'workflowLibrary', 'detail', id, 'list'] as const,
    category: (orgId: string) => [orgId, 'workflowLibrary', 'category'] as const,
    tags: (orgId: string) => [orgId, 'workflowLibrary', 'tags'] as const,
  },

  // Config Presets
  configPresets: {
    all: (orgId: string) => [orgId, 'configPresets'] as const,
    list: (orgId: string, nodeTypeId?: string) =>
      [orgId, 'configPresets', 'list', nodeTypeId || ''] as const,
    detail: (orgId: string, presetId: string) =>
      [orgId, 'configPresets', 'detail', presetId] as const,
  },

  // Centralized component config
  componentConfigs: {
    all: (orgId: string) => [orgId, 'componentConfigs'] as const,
    list: (orgId: string) => [orgId, 'componentConfigs', 'list'] as const,
    detail: (orgId: string, id: string) => [orgId, 'componentConfigs', 'detail', id] as const,
  },

  // Workflow Nodes
  // LEGACY: kept for useNodeResult (nodesApi.getResult — GET /node/{id}/result/),
  // not present in the reference project.
  nodes: {
    all: (orgId: string) => [orgId, 'nodes'] as const,
    result: (orgId: string, id: string) => [orgId, 'nodes', 'result', id] as const,
  },

  // Dashboard
  dashboard: {
    all: (orgId: string) => [orgId, 'dashboard'] as const,
    data: (orgId: string) => [orgId, 'dashboard', 'data'] as const,
  },

  // Asset Insights (asset + IP detail pages)
  assetInsights: {
    all: (orgId: string) => [orgId, 'assetInsights'] as const,
    detail: (orgId: string, kind: InsightsKind, id: string) =>
      [orgId, 'assetInsights', kind, 'detail', id] as const,
    techExposure: (orgId: string, kind: InsightsKind, id: string) =>
      [orgId, 'assetInsights', kind, 'techExposure', id] as const,
  },

  // Attack Surface
  attackSurface: {
    all: (orgId: string) => [orgId, 'attackSurface'] as const,
    overview: (orgId: string, params: string) => [orgId, 'attackSurface', 'overview', params] as const,
  },

  // Scans
  scans: {
    all: (orgId: string) => [orgId, 'scans'] as const,
    list: (orgId: string, params: string) => [orgId, 'scans', 'list', params] as const,
    detail: (orgId: string, id: string) => [orgId, 'scans', 'detail', id] as const,
    schedule: (orgId: string) => [orgId, 'scans', 'schedule'] as const,
    scheduleCalendar: (orgId: string, startDate: string, endDate: string) => [orgId, 'scans', 'scheduleCalendar', startDate, endDate] as const,
    dag: (orgId: string, params: string) => [orgId, 'scans', 'dag', params] as const,
    chart: (orgId: string) => [orgId, 'scans', 'chart'] as const,
    filterOptions: (orgId: string, key: string) => [orgId, 'scans', 'filterOptions', key] as const,
    allWorkflowsForScan: (orgId: string) => [orgId, 'scans', 'workflowsForScan'] as const,
    workflowsForScan: (orgId: string, page: number) => [orgId, 'scans', 'workflowsForScan', page] as const,
    // LEGACY: kept for useWorkflowAvailableTargets (workflow/target
    // compatibility filtering, see legacy item 3).
    workflowAvailableTargets: (orgId: string, workflowId: string) => [orgId, 'scans', 'workflowAvailableTargets', workflowId] as const,
    // Prefix key — invalidate this to refresh every page/search/portType
    // variant of the scannable-asset list (step 1 of Create Scan, RunWorkflowModal).
    allScannableAssets: (orgId: string) => [orgId, 'scans', 'scannableAssets'] as const,
    scannableAssets: (orgId: string, page: number) => [orgId, 'scans', 'scannableAssets', page] as const,
    nodeResult: (orgId: string, scanId: string, nodeId: string) =>
      [orgId, 'scans', 'detail', scanId, 'node-result', nodeId] as const,
  },

  // Runs
  runs: {
    all: (orgId: string) => [orgId, 'runs'] as const,
    // Keyed by scan id AND flow (orchestrator run) id.
    detail: (orgId: string, scanId: string, flowId: string) =>
      [orgId, 'runs', 'detail', scanId, flowId] as const,
  },

  // Leaked Data
  leaked: {
    all: (orgId: string) => [orgId, 'leaked'] as const,
    hosts: (orgId: string, page: number, params: string) => [orgId, 'leaked', page, params] as const,
    detail: (orgId: string, id: string) => [orgId, 'leaked', 'detail', id] as const,
    hostDetail: (orgId: string, hostId: string, page: number) => [orgId, 'leaked', 'host', hostId, page] as const,
  },

  // Tickets
  tickets: {
    all: (orgId: string) => [orgId, 'tickets'] as const,
    list: (orgId: string, params: string) => [orgId, 'tickets', 'list', params] as const,
    detail: (orgId: string, id: string) => [orgId, 'tickets', 'detail', id] as const,
    filterOptions: (orgId: string) => [orgId, 'tickets', 'filterOptions'] as const,
  },

  // Reports
  reports: {
    all: (orgId: string) => [orgId, 'reports'] as const,
    history: (orgId: string, params: string) => [orgId, 'reports', 'history', params] as const,
    historyInfinite: (orgId: string, params?: string) => [orgId, 'reports', 'history', 'infinite', params ?? ''] as const,
    tags: (orgId: string, search?: string) => [orgId, 'reports', 'tags', search ?? ''] as const,
    defaultName: (orgId: string, key: string) => [orgId, 'reports', 'defaultName', key] as const,
    detail: (orgId: string, id: string) => [orgId, 'reports', 'detail', id] as const,
    scanReports: (orgId: string, params: string) => [orgId, 'reports', 'scanReports', params] as const,
    scanReportsInfinite: (orgId: string) => [orgId, 'reports', 'scanReports', 'infinite'] as const,
    templates: (orgId: string, params?: string) => [orgId, 'reports', 'templates', params || ''] as const,
    templatesAll: (orgId: string) => [orgId, 'reports', 'templates'] as const,
    templateDetail: (orgId: string, id: string) => [orgId, 'reports', 'template', 'detail', id] as const,
  },

  // Other
  scope: {
    all: (orgId: string) => [orgId, 'scope'] as const,
    list: (orgId: string) => [orgId, 'scope', 'list'] as const,
  },
  exposures: {
    all: (orgId: string) => [orgId, 'exposures'] as const,
    data: (orgId: string) => [orgId, 'exposures', 'data'] as const,
  },
  integrations: {
    all: (orgId: string) => [orgId, 'integrations'] as const,
    list: (orgId: string, params?: string) => [orgId, 'integrations', 'list', params || ''] as const,
    detail: (orgId: string, id: string) => [orgId, 'integrations', 'detail', id] as const,
    fieldMapping: (orgId: string) => [orgId, 'integrations', 'fieldMapping'] as const,
    providers: (orgId: string) => [orgId, 'integrations', 'providers'] as const,
    events: (orgId: string, id: string, page: number) => [orgId, 'integrations', 'events', id, page] as const,
    statistics: (orgId: string, id: string) => [orgId, 'integrations', 'statistics', id] as const,
  },
  services: {
    all: (orgId: string) => [orgId, 'services'] as const,
    list: (orgId: string, params?: string) => [orgId, 'services', 'list', params || ''] as const,
  },
  targets: {
    all: (orgId: string) => [orgId, 'targets'] as const,
    list: (orgId: string, params: string) => [orgId, 'targets', 'list', params] as const,
    detail: (orgId: string, id: string) => [orgId, 'targets', 'detail', id] as const,
    verificationCode: (orgId: string, id: string) => [orgId, 'targets', 'verificationCode', id] as const,
  },
  targetTypes: {
    all: (orgId: string) => [orgId, 'targetTypes'] as const,
    list: (orgId: string) => [orgId, 'targetTypes', 'list'] as const,
  },
  pricePlans: {
    all: (orgId: string) => [orgId, 'pricePlans'] as const,
    list: (orgId: string) => [orgId, 'pricePlans', 'list'] as const,
  },
  paymentHistory: {
    all: (orgId: string) => [orgId, 'paymentHistory'] as const,
    list: (orgId: string) => [orgId, 'paymentHistory', 'list'] as const,
  },
  notifications: {
    all: (orgId: string) => [orgId, 'notifications'] as const,
    list: (orgId: string) => [orgId, 'notifications', 'list'] as const,
  },

  notificationConfig: {
    all: (orgId: string) => [orgId, 'notificationConfig'] as const,
    detail: (orgId: string) => [orgId, 'notificationConfig', 'detail'] as const,
  },

  notificationRules: {
    all: (orgId: string) => [orgId, 'notificationRules'] as const,
    list: (orgId: string) => [orgId, 'notificationRules', 'list'] as const,
    logs: (orgId: string, ruleId: string) => [orgId, 'notificationRules', 'logs', ruleId] as const,
  },

  notificationEventTypes: {
    all: (orgId: string) => [orgId, 'notificationEventTypes'] as const,
    list: (orgId: string) => [orgId, 'notificationEventTypes', 'list'] as const,
  },
  dns: {
    all: (orgId: string) => [orgId, 'dns'] as const,
    list: (orgId: string) => [orgId, 'dns', 'list'] as const,
  },
  /**
   * DNS Overrides.
   *
   * DELIBERATELY NOT org-scoped, unlike every other resource key in this file.
   * Custom DNS overrides are deployment-wide: the same records apply to every
   * organization. Adding orgId here would split the cache per org and refetch
   * identical data on every org switch.
   * See docs/superpowers/specs/2026-08-03-dns-overrides-design.md §3.3
   */
  dnsOverrides: {
    all: () => ['dnsOverrides'] as const,
    status: () => ['dnsOverrides', 'status'] as const,
    list: (params: string) => ['dnsOverrides', 'list', params] as const,
  },
  smtp: {
    all: (orgId: string) => [orgId, 'smtp'] as const,
    list: (orgId: string, params: number | string) => [orgId, params, 'smtp', 'list'] as const,
  },

  // Chat/AI Assistant
  chat: {
    all: (orgId: string) => [orgId, 'chat'] as const,
    /**
     * Takes the params OBJECT, not a pre-serialised string. React Query hashes
     * a key with `hashKey`, whose JSON replacer sorts plain-object keys
     * (`query-core/build/modern/utils.js:85-93`), so `{limit, offset}` and
     * `{offset, limit}` hash identically. Passing `JSON.stringify(params)`
     * instead hands it an opaque string, defeating that sort and splitting one
     * logical page across two cache entries and two requests.
     */
    list: (orgId: string, params?: PageParams) => [orgId, 'chat', 'list', params ?? {}] as const,
    detail: (orgId: string, chatId: string) => [orgId, 'chat', 'detail', chatId] as const,
    messages: (orgId: string, chatId: string) => [orgId, 'chat', 'messages', chatId] as const,
    /**
     * Chats anchored to one subject — the finding->chat lookup (doc §7) that
     * replaces the dead `vulnerability.chat_id` field.
     *
     * Every key here starts `[orgId, 'chat', …]`, so `all(orgId)` is a genuine
     * prefix of all four and one `invalidateQueries({queryKey: all(orgId)})`
     * reaches list, detail, messages and bySubject alike.
     */
    bySubject: (orgId: string, subjectType: string, subjectId: string) =>
      [orgId, 'chat', 'bySubject', subjectType, subjectId] as const,
  },

  // Authentication
  auth: {
    all: ['auth'] as const,
  },

  // Two-factor authentication
  twoFactor: {
    all: ['twoFactor'] as const,
    status: ['twoFactor', 'status'] as const,
    challengeEnrollment: ['twoFactor', 'challengeEnrollment'] as const,
  },
  // User Profile
  user: {
    all: ['user'] as const,
    profile: ['user', 'profile'] as const,
    permissions: ['user', 'permissions'] as const,
  },
  // User Profile
  userProfile: {
    all: ['userProfile'] as const,
    profile: ['userProfile', 'profile'] as const,
    permissions: ['userProfile', 'permissions'] as const,
  },

  // Security
  security: {
    all: ['security'] as const,
    loginAttempts: ['security', 'loginAttempts'] as const,
    devices: ['security', 'devices'] as const,
  },

  // Notification Settings
  notificationSettings: {
    all: ['notificationSettings'] as const,
  },

  // Payments
  payments: {
    all: (orgId: string) => [orgId, 'payments'] as const,
    list: (orgId: string, params?: string) => [orgId, 'payments', 'list', params || ''] as const,
    detail: (orgId: string, id: string) => [orgId, 'payments', 'detail', id] as const,
  },

  // Invoices
  invoices: {
    all: (orgId: string) => [orgId, 'invoices'] as const,
    list: (orgId: string, params?: string) => [orgId, 'invoices', 'list', params || ''] as const,
    detail: (orgId: string, id: string) => [orgId, 'invoices', 'detail', id] as const,
  },

  // Multi-Org Dashboard
  multiOrgDashboard: {
    all: ['multiOrgDashboard'] as const,
    data: ['multiOrgDashboard', 'data'] as const,
  },

  // Organizations
  organizations: {
    all: ['organizations'] as const,
    list: ['organizations', 'list'] as const,
    detail: (id: string) => ['organizations', 'detail', id] as const,
    current: ['organizations', 'current'] as const,
  },

  // IP Ranges
  ipRanges: {
    all: ['ipRanges'] as const,
    list: (orgId: string) => ['ipRanges', orgId] as const,
    detail: (orgId: string, id: string) => ['ipRanges', 'detail', orgId, id] as const,
    parentRanges: (orgId: string) => ['ipRanges', 'parentRanges', orgId] as const,
  },

  // Organization Memberships
  memberships: {
    all: ['memberships'] as const,
    list: (orgId: string, includeDescendants?: boolean) =>
      ['memberships', 'list', orgId, includeDescendants] as const,
    detail: (orgId: string, id: string) => ['memberships', 'detail', orgId, id] as const,
  },

  // Organization Roles
  roles: {
    all: ['roles'] as const,
    list: (orgId: string, includeInactive?: boolean) =>
      ['roles', 'list', orgId, includeInactive] as const,
    detail: (orgId: string, id: string) => ['roles', 'detail', orgId, id] as const,
    members: (orgId: string, roleId: string) => ['roles', 'members', orgId, roleId] as const,
  },

  // Permissions
  permissions: {
    all: ['permissions'] as const,
    user: ['permissions', 'user'] as const,
    rolePermissions: (orgId: string, roleId: string) =>
      ['permissions', 'role', orgId, roleId] as const,
    objectPermissions: (orgId: string, resourceType: string, roleId?: string, params?: string) =>
      ['permissions', 'object', orgId, resourceType, params || ''] as const,
    componentAccess: (orgId: string) => ['permissions', 'componentAccess', orgId] as const,
    roleComponents: (orgId: string, roleId: string) =>
      ['permissions', 'roleComponents', orgId, roleId] as const,
    objectResourceTypes: (orgId: string) =>
      ['permissions', 'objectResourceTypes', orgId] as const,
  },

  // Resource Limits
  resourceLimits: {
    all: ['resourceLimits'] as const,
    organizationUsage: (orgId: string, resourceType: string) =>
      ['resourceLimits', 'orgUsage', orgId, resourceType] as const,
    userLimits: (orgId: string, userId?: string) =>
      ['resourceLimits', 'userLimits', orgId, userId] as const,
    userUsage: (orgId: string, resourceType: string, userId?: string) =>
      ['resourceLimits', 'userUsage', orgId, resourceType, userId] as const,
  },
}
